import React from "react";
import {
  ArrowLeft,
  Shield,
  Lock,
  Eye,
  Database,
  UserCheck,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";
import { useNavigate } from "react-router-dom";

const Privacy = () => {
  const lastUpdated = "January 15, 2025";
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      <HeaderComponent />
      <section className="py-12 px-4 bg-gradient-to-r from-orange-500 to-red-500 text-white">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-white hover:text-orange-100 transition-all mr-6"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-2xl">
              🔒
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl">
            Your privacy and data security are sacred to us
          </p>
          <p className="text-sm mt-4 opacity-90">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            {/* Introduction */}
            <div className="mb-12 p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <Shield className="h-6 w-6 text-blue-500 mr-3 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Our Commitment to Your Privacy
                  </h3>
                  <p className="text-gray-600">
                    At MahalaxmiPuja, we respect your privacy and are committed
                    to protecting your personal information. We collect only the
                    minimum data necessary to provide authentic temple services
                    and never use your information for marketing or commercial
                    purposes beyond our spiritual services.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-10">
              {/* Section 1 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    1
                  </span>
                  Information We Collect
                </h2>
                <div className="text-gray-600 space-y-4 ml-11">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                      <Database className="h-5 w-5 mr-2" />
                      Personal Information During Signup
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-green-700">
                      <li>Full name (for personalized puja ceremonies)</li>
                      <li>
                        Email address (for booking confirmations and videos)
                      </li>
                      <li>
                        Phone number (for WhatsApp updates and pandit contact)
                      </li>
                      <li>Complete address (for prasad delivery)</li>
                      <li>
                        Gotra (optional, for traditional puja customization)
                      </li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                      <UserCheck className="h-5 w-5 mr-2" />
                      Booking and Service Information
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-blue-700">
                      <li>Puja service selections and preferences</li>
                      <li>Booking dates, times, and special instructions</li>
                      <li>Payment status and transaction details</li>
                      <li>Service frequency and usage patterns</li>
                      <li>Communication history with our support team</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">
                      What We DO NOT Collect
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      <li>Browsing behavior or website analytics</li>
                      <li>Social media profiles or external account data</li>
                      <li>Location tracking or device information</li>
                      <li>Third-party marketing data</li>
                      <li>Unnecessary personal preferences or demographics</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    2
                  </span>
                  How We Use Your Information
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p className="font-medium text-gray-800">
                    We use your personal information EXCLUSIVELY for providing
                    temple services:
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Puja Personalization:</strong> Your name and
                        gotra are used to perform personalized rituals with
                        proper dedication
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Service Delivery:</strong> Contact details for
                        booking confirmations, pandit communication, and video
                        sharing
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Prasad Delivery:</strong> Address information
                        for shipping blessed items and sacred materials
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Payment Processing:</strong> Transaction details
                        for booking confirmation and refund processing
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Customer Support:</strong> Service history for
                        better assistance and spiritual guidance
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                    <h4 className="font-semibold text-red-800 mb-2">
                      What We DO NOT Do
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-red-700">
                      <li>Sell your data to third parties</li>
                      <li>Use your information for marketing campaigns</li>
                      <li>Share data with advertising networks</li>
                      <li>Track your online behavior</li>
                      <li>Send promotional emails without consent</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    3
                  </span>
                  Data Sharing and Disclosure
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    <strong>
                      We do NOT share your personal information with third
                      parties
                    </strong>{" "}
                    except in the following limited circumstances:
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Temple Pandits:</strong> Only necessary details
                        (name, gotra, service type) are shared with performing
                        pandits for ritual purposes
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Delivery Partners:</strong> Address and contact
                        details for prasad delivery, handled by trusted courier
                        services
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Payment Processors:</strong> Transaction data
                        for secure payment processing, governed by their privacy
                        policies
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Legal Requirements:</strong> When required by
                        law or to protect our legal rights and temple property
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    4
                  </span>
                  Data Security
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    We take the security of your personal information seriously
                    and implement appropriate measures to protect it:
                  </p>

                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                        <Lock className="h-5 w-5 mr-2" />
                        Technical Safeguards
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-green-700 text-sm">
                        <li>SSL encryption for data transmission</li>
                        <li>Secure database storage</li>
                        <li>Regular security updates</li>
                        <li>Access controls and authentication</li>
                      </ul>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                        <Shield className="h-5 w-5 mr-2" />
                        Operational Safeguards
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
                        <li>Limited staff access to personal data</li>
                        <li>Regular data backups</li>
                        <li>Staff training on privacy protection</li>
                        <li>Incident response procedures</li>
                      </ul>
                    </div>
                  </div>

                  <p className="mt-4">
                    While we implement strong security measures, no method of
                    transmission over the internet is 100% secure. We
                    continuously work to improve our security practices.
                  </p>
                </div>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    5
                  </span>
                  Data Retention
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    We retain your personal information only as long as
                    necessary to provide our services and fulfill our spiritual
                    commitments to you:
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Account Information:</strong> Retained while
                        your account is active and for 2 years after last
                        service for support purposes
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Booking History:</strong> Maintained for 3 years
                        to provide service history and spiritual guidance
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Payment Records:</strong> Kept for 7 years as
                        required by financial regulations and tax compliance
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Video Recordings:</strong> Personal puja videos
                        are stored securely and accessible to you indefinitely
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    6
                  </span>
                  Your Privacy Rights
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    You have the following rights regarding your personal
                    information:
                  </p>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <Eye className="h-5 w-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <strong>Access:</strong> Request to see what personal
                          information we have about you
                        </div>
                      </div>

                      <div className="flex items-start">
                        <span className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0">
                          ✏️
                        </span>
                        <div>
                          <strong>Correction:</strong> Request to update or
                          correct your personal information
                        </div>
                      </div>

                      <div className="flex items-start">
                        <span className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0">
                          🗑️
                        </span>
                        <div>
                          <strong>Deletion:</strong> Request deletion of your
                          account and personal data (subject to legal and
                          spiritual service requirements)
                        </div>
                      </div>

                      <div className="flex items-start">
                        <span className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0">
                          📥
                        </span>
                        <div>
                          <strong>Portability:</strong> Request a copy of your
                          data in a portable format
                        </div>
                      </div>

                      <div className="flex items-start">
                        <span className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0">
                          ⛔
                        </span>
                        <div>
                          <strong>Objection:</strong> Object to certain uses of
                          your personal information
                        </div>
                      </div>
                    </div>
                  </div>

                  <p>
                    To exercise any of these rights, please contact us using the
                    information provided at the end of this policy.
                  </p>
                </div>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    7
                  </span>
                  Cookies and Tracking
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    <strong>We use minimal tracking technology:</strong>
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Essential Cookies Only:</strong> Basic cookies
                        required for login and cart functionality
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>No Analytics Tracking:</strong> We do not use
                        Google Analytics or similar tracking services
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>No Advertising Cookies:</strong> We do not place
                        advertising or marketing cookies
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>No Social Media Tracking:</strong> We do not
                        embed social media tracking pixels
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    8
                  </span>
                  Children's Privacy
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    Our services are intended for individuals 18 years and
                    older. We do not knowingly collect personal information from
                    children under 18.
                  </p>
                  <p>
                    If a parent or guardian becomes aware that their child has
                    provided us with personal information without consent,
                    please contact us immediately and we will delete such
                    information from our records.
                  </p>
                  <p>
                    Children may participate in puja ceremonies when bookings
                    are made by their parents or guardians, but their personal
                    data is not collected separately.
                  </p>
                </div>
              </div>

              {/* Section 9 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    9
                  </span>
                  Changes to Privacy Policy
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    We may update this Privacy Policy from time to time to
                    reflect changes in our practices or legal requirements.
                  </p>
                  <p>
                    Your continued use of our services after such modifications
                    constitutes acceptance of the updated Privacy Policy.
                  </p>
                </div>
              </div>

              {/* Section 10 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    10
                  </span>
                  Contact Us
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    If you have any questions about this Privacy Policy or how
                    we handle your personal information, please contact us:
                  </p>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Phone:</span>
                        <span className="ml-2">+91 9699588995</span>
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Email:</span>
                        <span className="ml-2">
                          ambabaimahalaxmipuja@gmail.com
                        </span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Address:</span>
                        <span className="ml-2">
                          Shree Mahalaxmi Ambabai Temple, Kolhapur, Maharashtra
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm mt-4">
                    <strong>Response Time:</strong> We will respond to
                    privacy-related inquiries within 7 working days.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Notice */}
            <div className="mt-12 p-6 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg">
              <div className="flex items-start">
                <Lock className="h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold mb-2">
                    Your Trust is Sacred to Us
                  </h4>
                  <p className="text-sm">
                    We understand that sharing personal information requires
                    trust, especially for spiritual services. We are committed
                    to protecting your privacy with the same reverence we show
                    to our temple traditions. Your data is safe with us.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Privacy;
